import {
  DeleteOutlined,
  OrderedListOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Alert, Button, Form, FormRule, Select, Switch } from "antd";
import { NamePath } from "antd/es/form/interface";
import * as React from "react";
import { getErrorMessage } from "src/common/onError";
import {
  ZodArray,
  ZodBoolean,
  ZodDiscriminatedUnion,
  ZodEnum,
  ZodLazy,
  ZodLiteral,
  ZodNullable,
  ZodNumber,
  ZodObject,
  ZodOptional,
  ZodString,
} from "zod";
import { $ZodType } from "zod/v4/core";

import { AttrFeatures } from "../AttrFeatures";
import { findCustom } from "./attrEditorCustoms";
import * as styles from "./AttrMetaEditorForm.module.less";
import { FieldDef, itemName, makeLabel, mkField } from "./attrMetaEditorUtils";
import { BoolEd } from "./components/BoolEd";
import { EnumEd } from "./components/EnumEd";
import { NumberEd } from "./components/NumberEd";
import { StringEd } from "./components/StringEd";

/* eslint no-underscore-dangle: ["error", { "allow": ["_zod"] }]  */
/* eslint jsx-a11y/label-has-associated-control: "off" */

type PropsAttrMetaEditorNode = {
  node: $ZodType;
  path: NamePath;
  field?: FieldDef;
  attrFeatures: AttrFeatures;
};

export const AttrMetaEditorNode: React.FC<PropsAttrMetaEditorNode> = (
  props,
) => {
  const { node, path, field, attrFeatures } = props;

  const { def } = node._zod;
  if (node instanceof ZodOptional) {
    return (
      <AttrMetaEditorNode
        node={node.def.innerType}
        path={path}
        field={mkField("optional", node, field)}
        attrFeatures={attrFeatures}
      />
    );
  }
  if (node instanceof ZodNullable) {
    return (
      <AttrMetaEditorNode
        node={node.def.innerType}
        path={path}
        field={mkField("nullable", node, field)}
        attrFeatures={attrFeatures}
      />
    );
  }

  const Comp = findCustom(path)?.Comp;
  if (Comp) {
    return <Comp path={path} node={node} field={field} />;
  }

  if (node instanceof ZodLazy) {
    return (
      <AttrMetaEditorNode
        node={node.def.getter()}
        path={path}
        attrFeatures={attrFeatures}
      />
    );
  }
  if (node instanceof ZodObject) {
    return (
      <ObjectEd
        node={node}
        path={path}
        field={field}
        attrFeatures={attrFeatures}
      />
    );
  }
  if (node instanceof ZodString) {
    return <StringEd node={node} field={field} path={path} />;
  }

  if (node instanceof ZodDiscriminatedUnion) {
    return (
      <DiscUnionEd
        node={node}
        path={path}
        field={field}
        attrFeatures={attrFeatures}
      />
    );
  }

  if (node instanceof ZodBoolean) {
    return <BoolEd node={node} path={path} field={field} />;
  }
  if (node instanceof ZodNumber) {
    return <NumberEd node={node} path={path} field={field} />;
  }
  if (node instanceof ZodEnum) {
    return <EnumEd node={node} path={path} field={field} />;
  }
  if (node instanceof ZodArray) {
    return (
      <ArrayEd
        node={node}
        path={path}
        field={field}
        attrFeatures={attrFeatures}
      />
    );
  }

  return (
    <Alert
      type="error"
      message={`Invalid node type: ${def.type} for ${path.join(".")}`}
    />
  );
};

// Рекурсивные компоненты не выносятся в отдельные файлы, чтобы не создавались циклические зависимости.

// Объект рекурсивно вызывает AttrMetaEditorNode
type PropsObjectEd = {
  node: ZodObject;
  path: NamePath;
  field?: FieldDef;
  ignore?: string;
  internal?: boolean;
  attrFeatures: AttrFeatures;
};
const ObjectEd: React.FC<PropsObjectEd> = (props) => {
  const { node, path, field, ignore, internal, attrFeatures } = props;
  const label = makeLabel(node, path, field);
  const content = () =>
    Object.entries(node.shape)
      .filter(([name]) => name !== ignore)
      .map(([name, value]) => (
        <AttrMetaEditorNode
          key={name}
          node={value}
          path={[...path, name]}
          attrFeatures={attrFeatures}
        />
      ));
  const optional = field?.nullable || field?.optional;
  if (optional)
    return <OptionalObjectEd path={path} label={label} content={content} />;
  if (!path.length || internal) return content();
  return (
    <div className={styles.block}>
      <label>{label}</label>
      <div>{content()}</div>
    </div>
  );
};

type PropsOptionalObjectEd = {
  path: string[];
  label: string;
  content: () => React.ReactNode;
};
const OptionalObjectEd: React.FC<PropsOptionalObjectEd> = (props) => {
  const { path, label, content } = props;
  const form = Form.useFormInstance();
  const objValue = Form.useWatch(path, { preserve: true });
  const used = !!objValue;
  return (
    <div className={styles.block}>
      <label>
        <span style={{ flex: 1 }}>{label}</span>
        <Switch
          value={used}
          unCheckedChildren="Не используется"
          checkedChildren="Используется"
          onChange={(value) => {
            form.setFieldValue(path, value ? {} : null);
          }}
        />
      </label>
      {used && <div>{content()}</div>}
    </div>
  );
};

type DiscOption = {
  value: string;
  label: string;
  obj: ZodObject;
};
const makeDiscOptions = (node: ZodDiscriminatedUnion): DiscOption[] => {
  const { discriminator, options } = node.def;
  return options.map((op, i) => {
    const { def } = op._zod;
    if (def.type !== "object")
      throw Error(`Expected object option, but got ${def.type}`);
    const obj = op as ZodObject;
    const pair = Object.entries(obj.shape).find(
      ([key]) => key === discriminator,
    );
    if (!pair)
      throw Error(`Discriminator ${discriminator} not found in option ${i}`);
    const [, opValue] = pair;
    const lit = opValue as ZodLiteral;
    const name = String(lit.def.values[0]);
    let label = name;
    if (lit.description) label += ` (${lit.description})`;
    return { value: name, label, obj };
  });
};

// Discriminated union рекурсивно вызывает ObjectEd
type PropsDiscUnionEd = {
  node: ZodDiscriminatedUnion;
  path: string[];
  field?: FieldDef;
  attrFeatures: AttrFeatures;
};
const DiscUnionEd: React.FC<PropsDiscUnionEd> = (props) => {
  const { node, path, field, attrFeatures } = props;
  const { discriminator } = node.def;
  const discPath = [...path, discriminator];
  const curValue = Form.useWatch(discPath, { preserve: true });
  const rules: FormRule[] = [];
  if (!field?.optional) {
    rules.push({ required: true, message: "Необходимо выбрать вариант" });
  }
  let options: DiscOption[] = [];
  try {
    options = makeDiscOptions(node);
  } catch (e) {
    return <Alert type="error" {...getErrorMessage(e)} />;
  }
  const curOption = options.find(({ value }) => value === curValue);
  const custom = findCustom(path);
  const CustomSelect = custom?.Select;
  return (
    <div className={styles.union}>
      {field?.optional && (
        <Form.Item
          name={itemName([...path, `_discriminator#${discriminator}`])}
          hidden
        />
      )}
      <Form.Item
        name={itemName(discPath)}
        label={makeLabel(node, path, field)}
        rules={rules}
        tooltip={custom?.tooltip}
      >
        {CustomSelect ? (
          <CustomSelect path={path} attrFeatures={attrFeatures} />
        ) : (
          <Select options={options} allowClear title={path.join(".")} />
        )}
      </Form.Item>
      {curOption && (
        <ObjectEd
          node={curOption.obj}
          path={path}
          ignore={discriminator}
          internal
          attrFeatures={attrFeatures}
        />
      )}
    </div>
  );
};

// Массив рекурсивно вызывает AttrMetaEditorNode
type PropsArrayEd = {
  node: ZodArray;
  path: NamePath;
  field?: FieldDef;
  attrFeatures: AttrFeatures;
};
const ArrayEd: React.FC<PropsArrayEd> = (props) => {
  const { node, path, field, attrFeatures } = props;
  const label = makeLabel(node, path, field);
  return (
    <Form.List name={itemName(path)}>
      {(fields, { add, remove }) => (
        <div className={styles.block}>
          <label>
            <OrderedListOutlined />
            <span style={{ flex: 1 }}>{label}</span>
            <Button
              icon={<PlusOutlined />}
              size="small"
              onClick={() => add(createArrayItem(node.def.element))}
            >
              Добавить
            </Button>
          </label>
          <div>
            {fields.map((formField, index) => (
              <div key={formField.key} className={styles.item}>
                <div className={styles.itemContent}>
                  <AttrMetaEditorNode
                    node={node.def.element}
                    path={[...path, formField.name]}
                    attrFeatures={attrFeatures}
                  />
                </div>
                <div>
                  <Button
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => remove(index)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Form.List>
  );
};
const createArrayItem = (item: $ZodType): unknown => {
  if (item instanceof ZodLazy) {
    return createArrayItem(item.def.getter());
  }
  if (item instanceof ZodObject) {
    // Это нужно потому что иначе не сработает ZAttrMeta.parse
    // Эти поля обязательные, но заполняются на сервере. Поэтому они запрещены в UI.
    //  И им принудительно присваиваются дефолтные значения.
    if ("uid" in item.shape && "displayId" in item.shape) {
      return { uid: "", displayId: 0 };
    }
  }
  return null;
};
